import React, { useMemo } from 'react';
import classNames from 'classnames';
import useIntls from '@opm/hooks/useIntls';
import CardCommon from '@op/components/CardCommon';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import KPICell from '@opm/components/KPICell';
import type { TGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { TREND_CARD_KPI_CONFIGS, TTrendCardKPIConfigItem } from '../constants';
import { TTopicTypeColorMap } from '../utils';
import { ITopCreative, IListMax, ITrendItemBase } from '../types';
import TrendTopicTags from '../components/TrendTopicTags';
import AISummaryBlock from '../components/AISummaryBlock';
import { useGameMeta } from '../components/useGameMeta';
import VideoCover from './VideoCover';
import SvgIcon from '@home/components/SvgIcon';
import { DBO_V } from '@op/utils/format';
interface ITrendCardProps {
  data: ITrendItemBase;
  /** 当前 tab + filter 下全量 KPI 最大值（进度条归一化） */
  listMax?: IListMax;
  /** 游戏元信息字典（cond.materials_config_games）：id → meta */
  gameMap?: TGameMap;
  /** Topic Type chip 颜色映射（按 L1 parent 取色，L2 共享 parent 色） */
  topicTypeColorMap?: TTopicTypeColorMap;
  onClick?: (record: ITrendItemBase) => void;
  onTitleClick?: (record: ITrendItemBase) => void;
  onThumbnailClick?: (item: ITopCreative, record: ITrendItemBase) => void;
  selected?: boolean;
  filter: any;
  kpiConfigs?: TTrendCardKPIConfigItem[];
  /**
   * 缩略图数组取值器（默认 `record.top_related_creatives`）。
   * UGC 等扩展页可注入 `(r) => r.top_related_videos`。
   */
  thumbnailsAccessor?: (record: ITrendItemBase) => any[] | undefined;
  renderThumbnail?: (args: {
    item: any;
    record: ITrendItemBase;
    index: number;
    filter: any;
    onThumbnailClick?: (item: ITopCreative, record: ITrendItemBase) => void;
  }) => React.ReactNode;
}

/**
 * 单个趋势卡片（按 Figma 3902_52416 设计实现）
 *
 * 布局（自上而下）：
 *  ┌──────────────────────────────────────────────────┐
 *  │ Title →                            🎮 Vita Mahjong │
 *  │ [tag1] [tag2]                                     │
 *  ├──────────────────────────────────────────────────┤
 *  │ Impressions    Creatives    Avg.Days Active        │
 *  │ 999.99M        999.99M      999.99M                │
 *  │ DoD ↑88%       DoD ↑88%                           │
 *  ├──────────────────────────────────────────────────┤
 *  │ ✨ AI Summary                                     │
 *  │ <gradient bg> 文本 ........                       │
 *  ├──────────────────────────────────────────────────┤
 *  │ ┌────┐ ┌────┐ ┌────┐                             │
 *  │ │imp │ │imp │ │imp │  ← 3 张缩略图（顶部 imp/likes 遮罩）│
 *  │ │    │ │    │ │    │                              │
 *  │ │date│ │date│ │date│  ← 底部日期遮罩                │
 *  │ └────┘ └────┘ └────┘                             │
 *  └──────────────────────────────────────────────────┘
 */
export default function TrendCard({
  data,
  gameMap,
  topicTypeColorMap,
  onClick,
  onTitleClick,
  onThumbnailClick,
  selected,
  filter,
  kpiConfigs = TREND_CARD_KPI_CONFIGS,
  thumbnailsAccessor,
  renderThumbnail,
}: ITrendCardProps) {
  const thumbList: any[] =
    (thumbnailsAccessor
      ? thumbnailsAccessor(data)
      : (data as any)?.top_related_creatives) || [];
  const { intl, isEn } = useIntls();
  // topic：后端按多语言下发 topic（zh）/ topic_en（en），按当前语言取值
  const title = useMemo(
    () => (isEn ? data?.topic_en || data?.topic : data?.topic),
    [isEn, data?.topic_en, data?.topic],
  );

  const { gameName: displayGameName, gameLogo: displayGameLogo } = useGameMeta(data, gameMap);

  return (
    <CardCommon
      className={classNames('dbom-ct-card', {
        'dbom-ct-card--selected': selected,
      })}
      onClick={() => onClick?.(data)}
      title={
        <div
          className={'card-slot-title-hover'}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onTitleClick?.(data);
          }}
        >
          <span>{title}</span>
          <SvgIcon type="arrow-right" className="card-slot-icon" />
        </div>
      }
      dropdownsFilterBeforeElement={
        <>
          {displayGameName !== '-' && (
            <div className="dbom-ct-card__game">
              <RenderGameIcon
                className="dbom-ct-card__game-logo"
                gameInfo={{
                  cover: displayGameLogo,
                  entity_type: (data as any)?.entity_type,
                  edition_unified_id: data?.game_id,
                }}
              />
              <span className="dbom-ct-card__game-name" title={displayGameName}>
                {displayGameName}
              </span>
            </div>
          )}
        </>
      }
      subFilterBeforeElement={
        <div className="dbom-ct-card__tags">
          <TrendTopicTags
            record={data}
            topicTypeColorMap={topicTypeColorMap}
            chipClassName="dbom-ct-card__chip"
          />
        </div>
      }
    >
      {/* KPI 三列：复用 KPICell 共享组件 */}
      <div className="dbom-ct-card__kpi">
        {kpiConfigs.map(({ dataKey, labelKey, decimal }) => (
          <KPICell
            key={dataKey}
            className="dbom-ct-card__kpi-item"
            label={intl(labelKey)}
            formatValue={
              decimal !== undefined
                ? (value: number) => DBO_V.reviews(value, decimal)
                : undefined
            }
            kpiData={(data as any)?.[dataKey]}
            filter={filter}
          />
        ))}
      </div>

      {/* AI Summary */}
      <AISummaryBlock summaryData={data} maxLines={3} />

      {/* 缩略图 3 张（VideoCover：含 hover 双按钮） */}
      <div className="dbom-ct-card__thumbs">
        {thumbList.slice(0, 3).map((item: any, idx: number) => (
          <React.Fragment key={item?.video_url || idx}>
            {renderThumbnail ? (
              renderThumbnail({
                item,
                record: data,
                index: idx,
                filter,
                onThumbnailClick,
              })
            ) : (
              <VideoCover
                data={item}
                onPlay={(playItem) =>
                  onThumbnailClick?.(playItem as ITopCreative, data)
                }
                filter={filter}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </CardCommon>
  );
}
